import { Images } from '@/assets/images';
import { REGEX } from '@/constant';
import { cn, dayjs, formatDate } from '@/utils';

import { FormConfig } from '../define-document-write.type';

const initializeValue = {
	//Page 1
	registerDate: '',
	supervisor: '',
	position: '', // '군수',
	name: '',
	national: '',
	localGovernment: '',
	dateOfConclusion: '',
	preschoolPeriod: '',
	kindergartenSize: '',
	signature: '',
	contactLocalGovernment: '',
	conclusionProcess: '',

	//Page 2
	workerRecruitmentProcedure: '',
	workerSelectionMethod: '',
	transmissionProcedure: '',
	thirdPartyAuthorization: '',

	// Page 3
	languageCommunication: '',
	separateFee: '',
	unauthorizedDeparture: '',
	procedureSupport: '',
};

type IValues = typeof initializeValue;

export const BUSINESS_AGREEMENT_CONFIG: FormConfig<IValues> = {
	initializeValue,
	title: '업무협약(MOU) 체결 결과서',
	mapPropToValues({ detailData }) {
		const { unitInfo } = detailData || {};

		return {
			localGovernment: unitInfo?.name || '',

			registerDate: formatDate(dayjs()),
		};
	},
	pages: [
		{
			background: {
				src: Images.formBusinessAgreementPage1Highlight,
				className: 'template',
				dataSource: Images.formBusinessAgreementPage1,
			},
			textAreas: [
				(values, index) => ({
					name: 'supervisor',
					maxHeight: 28,
					maxLength: 8,
					className:
						'text-[14px] text-center focus:bg-white bg-transparent !h-[28px]',
					containerClassName: cn(
						'absolute top-[282px] right-[242px] w-[116px] z-[1]',
						values?.supervisor && 'bg-white'
					),
					regex: REGEX.ALLOW_KR_SPACE,
				}),
				(values, index) => ({
					name: 'position',
					maxHeight: 28,
					maxLength: 5,
					className: 'text-[14px] text-left focus:bg-white !h-[28px]',
					containerClassName: cn(
						'absolute top-[282px] right-[243px] w-[32px]',
						values?.supervisor && 'bg-white'
					),
					regex: REGEX.ALLOW_KR_SPACE,
					readOnly: true,
				}),
				(values, index) => ({
					name: 'name',
					maxHeight: 28,
					maxLength: 5,
					className: 'text-[14px] text-left focus:bg-white !h-[28px]',
					containerClassName: cn(
						'absolute top-[282px] right-[173px] w-[70px]',
						values?.name && 'bg-white'
					),
					regex: REGEX.ALLOW_KR,
				}),
				{
					name: 'national',
					maxHeight: 28,
					maxLength: 40,
					className: 'text-[14px] text-left',
					containerClassName: 'absolute top-[382px] left-[192px] w-[500px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				},
				{
					name: 'localGovernment',
					maxHeight: 28,
					maxLength: 40,
					className: 'text-[14px] text-left',
					containerClassName: 'absolute top-[413px] left-[192px] w-[500px]',
				},
				{
					name: 'preschoolPeriod',
					maxHeight: 40,
					maxLength: 60,
					className: 'text-[14px] text-left',
					containerClassName: 'absolute top-[485px] left-[320px] w-[370px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				},
				{
					name: 'kindergartenSize',
					maxHeight: 40,
					maxLength: 60,
					className: 'text-[14px] text-left',
					containerClassName: 'absolute top-[525px] left-[320px] w-[370px]',
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				},
				(values, index) => ({
					name: 'contactLocalGovernment',
					maxHeight: 130,
					maxLength: 200,
					className: 'text-[14px] text-left focus:bg-white !h-[130px]',
					containerClassName: cn(
						'absolute bottom-[300px] left-[105px] w-[590px]',
						values?.contactLocalGovernment && 'bg-white'
					),
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				}),
				(values, index) => ({
					name: 'conclusionProcess',
					maxHeight: 160,
					maxLength: 200,
					className: 'text-[14px] text-left focus:bg-white !h-[160px]',
					containerClassName: cn(
						'absolute bottom-[100px] left-[105px] w-[590px]',
						values?.conclusionProcess && 'bg-white'
					),
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				}),
			],
			datePickers: [
				{
					name: 'registerDate',
					segmentedInputs: true,
					format: 'YYYY        MM       DD',
					containerClassName: 'absolute top-[260px] left-[305px] ',
					style: { fontSize: '14px' },
				},
				{
					name: 'dateOfConclusion',
					className:
						'[&>.ant-picker-input>input]:!text-[14px] [&>.ant-picker-input>input]:!w-[220px]',
					format: 'YYYY-MM-DD',
					containerClassName: 'absolute top-[444px] left-[195px] ',
					style: {
						width: '90px',
						fontSize: '14px',
						marginTop: '0',
					},
				},
			],
			signatures: [
				{
					name: 'signature',
					className: 'absolute top-[265px] right-[92px] w-[60px] h-[50px]',
				},
			],
		},
		{
			background: {
				src: Images.formBusinessAgreementPage2Highlight,
				className: 'template',
				dataSource: Images.formBusinessAgreementPage2,
			},
			textAreas: [
				(values, index) => ({
					name: 'workerRecruitmentProcedure',
					maxHeight: 205,
					maxLength: 300,
					className: 'text-[14px] text-left focus:bg-white !h-[205px]',
					containerClassName: cn(
						'absolute top-[155px] left-[260px] w-[415px]',
						values?.workerRecruitmentProcedure && 'bg-white'
					),
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				}),
				(values, index) => ({
					name: 'workerSelectionMethod',
					maxHeight: 205,
					maxLength: 300,
					className: 'text-[14px] text-left focus:bg-white !h-[205px]',
					containerClassName: cn(
						'absolute top-[370px] left-[260px] w-[415px]',
						values?.workerSelectionMethod && 'bg-white'
					),
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				}),
				(values, index) => ({
					name: 'transmissionProcedure',
					maxHeight: 205,
					maxLength: 300,
					className: 'text-[14px] text-left focus:bg-white !h-[205px]',
					containerClassName: cn(
						'absolute top-[585px] left-[260px] w-[415px]',
						values?.transmissionProcedure && 'bg-white'
					),
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				}),
				(values, index) => ({
					name: 'thirdPartyAuthorization',
					maxHeight: 205,
					maxLength: 300,
					className: 'text-[14px] text-left focus:bg-white !h-[205px]',
					containerClassName: cn(
						'absolute top-[800px] left-[260px] w-[415px]',
						values?.thirdPartyAuthorization && 'bg-white'
					),
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				}),
			],
		},
		{
			background: {
				src: Images.formBusinessAgreementPage3Highlight,
				className: 'template',
				dataSource: Images.formBusinessAgreementPage3,
			},
			textAreas: [
				(values, index) => ({
					name: 'languageCommunication',
					maxHeight: 165,
					maxLength: 200,
					className: 'text-[14px] text-left focus:bg-white !h-[165px]',
					containerClassName: cn(
						'absolute top-[170px] left-[85px] w-[600px]',
						values?.languageCommunication && 'bg-white'
					),
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				}),
				(values, index) => ({
					name: 'separateFee',
					maxHeight: 220,
					maxLength: 200,
					className: 'text-[14px] text-left focus:bg-white !h-[220px]',
					containerClassName: cn(
						'absolute top-[370px] left-[85px] w-[600px]',
						values?.separateFee && 'bg-white'
					),
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				}),
				(values, index) => ({
					name: 'unauthorizedDeparture',
					maxHeight: 140,
					maxLength: 200,
					className: 'text-[14px] text-left focus:bg-white !h-[140px]',
					containerClassName: cn(
						'absolute top-[660px] left-[85px] w-[600px]',
						values?.unauthorizedDeparture && 'bg-white'
					),
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				}),
				(values, index) => ({
					name: 'procedureSupport',
					maxHeight: 130,
					maxLength: 200,
					className: 'text-[14px] text-left focus:bg-white !h-[130px]',
					containerClassName: cn(
						'absolute top-[835px] left-[85px] w-[600px]',
						values?.procedureSupport && 'bg-white'
					),
					regex: REGEX.ALLOW_KR_EN_NUMBER,
				}),
			],
		},
	],
};
